import { lazy, Suspense, useEffect, useState } from 'react'

import { cn } from '@/utils'
import { TrialDemoCover } from './TrialDemoCover'

// Fetched on the click, so the signup page does not carry the player.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'))

// The same Mux cut the marketing homepage plays (website-v2 `overviewVideo.ts`).
const DEMO_PLAYBACK_ID = 'lwYD9NUHEiNZEtse02kzncGg8FXqvifooUdH8hCn4LbQ'
const DEMO_TITLE = 'How CounselPro runs one matter'
const BRAND_GREEN = '#10c183'

// If the video never starts (blocked autoplay, slow network), stop covering the
// player so its own controls and errors show instead of a spinner forever.
const REVEAL_PLAYER_AFTER_MS = 8000

type DemoStage = 'cover' | 'starting' | 'playing'

const useDemoStage = () => {
  const [stage, setStage] = useState<DemoStage>('cover')
  const revealPlayer = () => setStage('playing')

  useEffect(() => {
    if (stage !== 'starting') return
    const timer = setTimeout(() => setStage('playing'), REVEAL_PLAYER_AFTER_MS)
    return () => clearTimeout(timer)
  }, [stage])

  return { stage, start: () => setStage('starting'), revealPlayer }
}

const DemoPlayer = ({ onStarted }: { onStarted: () => void }) => (
  <Suspense fallback={null}>
    <MuxPlayer
      className="size-full"
      streamType="on-demand"
      playbackId={DEMO_PLAYBACK_ID}
      title={DEMO_TITLE}
      metadata={{ video_title: DEMO_TITLE }}
      accentColor={BRAND_GREEN}
      autoPlay="any"
      playsInline
      defaultHiddenCaptions
      onPlaying={onStarted}
      onError={onStarted}
    />
  </Suspense>
)

export const TrialDemoVideo = () => {
  const { stage, start, revealPlayer } = useDemoStage()
  const isPlaying = stage === 'playing'
  return (
    <div className="relative hidden aspect-video overflow-hidden rounded-xl border bg-blue-7 shadow-sm lg:block">
      {stage !== 'cover' && <DemoPlayer onStarted={revealPlayer} />}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-500',
          isPlaying && 'pointer-events-none opacity-0',
        )}
      >
        <TrialDemoCover
          title={DEMO_TITLE}
          isStarting={stage === 'starting'}
          onPlay={start}
        />
      </div>
    </div>
  )
}
